import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {Router} from '@angular/router';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {CnReconciliationApiService} from '../../../service/api/cn-reconciliation-api/cn-reconciliation-api.service';
import {CnAbgleichRequestDto} from '../../../model/dto/api/cn-abgleich-request.dto';
import {CnAbgleichResultDto} from '../../../model/dto/api/cn-abgleich-result.dto';
import {CnModelExpectationDto} from '../../../model/dto/api/cn-model-expectation.dto';
import {CnContractComparisonDto} from '../../../model/dto/api/cn-contract-comparison.dto';
import {cnErrorMessage} from '../../../shared/cn-error-message.util';

/**
 * Soll/Ist-Abgleich result renderer, hosted as the second tab of CnModelComparisonPage.
 *
 * CONSUMES the selection passed from the Abgleich page (statementId, activeModelId, horizonYears,
 * scenarioModelIds). When statementId + activeModelId are present it calls
 * CnReconciliationApiService.abgleichVsModels(...) and renders the Ist-anchored comparison:
 *  - a verdict banner (most lucrative model + uplift vs. active, best match to Ist),
 *  - Summen-Kacheln (one Ist card + one card per model with expected total and Δ vs. Ist),
 *  - a "Je Vertrag" sub-tab (plain p-table over the transient perContract array),
 *  - a "Verlauf" sub-tab (monthly Ist bars + cumulative-Ist line + per-model horizontal reference
 *    lines at each model's flat portfolio total).
 *
 * NFR-CleanCore: NO business calculation in the FE. The only display transforms applied here are
 * EUR formatting, colour-by-sign, and the cumulative running-sum used for the chart's Ist line.
 * UI states: empty (no selection) / loading / ready / error.
 */
@Component({
    selector: 'mvs-cn-abgleich-comparison',
    templateUrl: './cn-abgleich-comparison.component.html',
    styleUrls: ['./cn-abgleich-comparison.component.scss'],
    standalone: false
})
export class CnAbgleichComparisonComponent implements OnInit, OnChanges, OnDestroy {

    /** Id of the CommissionStatement that supplies the received (Ist) amounts. */
    @Input() statementId: number | null = null;
    /** Id of the active (reference) commission model. */
    @Input() activeModelId: number | null = null;
    /** Forecast horizon in years (defaults to 5 when not supplied). */
    @Input() horizonYears: number = 5;
    /** Ids of the scenario commission models to compare against. */
    @Input() scenarioModelIds: number[] = [];

    /** UI state for the comparison: empty (no selection) / loading / ready / error. */
    state: 'empty' | 'loading' | 'ready' | 'error' = 'empty';
    errorMessage: string | null = null;

    /** Backend Soll/Ist-Abgleich result (null until loaded). */
    result: CnAbgleichResultDto | null = null;

    /** Active sub-tab of the result body (0 = Je Vertrag, 1 = Verlauf). */
    activeSubTab: number = 0;

    /** Chart.js data / options for the monthly Verlauf chart (null until built). */
    chartData: any = null;
    chartOptions: any = null;

    initialized: boolean = false;

    private destroy$ = new Subject<void>();

    constructor(
        private reconciliationApiService: CnReconciliationApiService,
        private router: Router
    ) {
    }

    ngOnInit(): void {
        this.chartOptions = this.buildChartOptions();
        this.initialized = true;
        this.loadComparison();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
        if (changes['statementId'] || changes['activeModelId']
            || changes['horizonYears'] || changes['scenarioModelIds']) {
            this.loadComparison();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /** Whether a selection is present that allows running the comparison. */
    hasSelection(): boolean {
        return this.statementId != null && this.activeModelId != null;
    }

    /**
     * Run the received-vs-models comparison from the bound selection and render the result. Without
     * a statement + active model it stays in the empty state (the template shows the back-link hint).
     */
    loadComparison(): void {
        if (!this.hasSelection()) {
            this.state = 'empty';
            this.result = null;
            this.chartData = null;
            return;
        }

        this.state = 'loading';
        this.errorMessage = null;
        this.result = null;
        this.chartData = null;

        const request: CnAbgleichRequestDto = {
            statementId: this.statementId!,
            activeModelId: this.activeModelId!,
            scenarioModelIds: this.scenarioModelIds ?? [],
            horizonYears: this.horizonYears && this.horizonYears > 0 ? this.horizonYears : 5
        };

        this.reconciliationApiService.abgleichVsModels(request)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (value) => {
                    this.result = value ?? null;
                    this.chartData = this.buildChartData(value);
                    this.state = 'ready';
                },
                error: (err) => {
                    this.state = 'error';
                    this.errorMessage = cnErrorMessage(err);
                }
            });
    }

    /** Re-run the comparison from the same selection ("Neu laden"). */
    reload(): void {
        this.loadComparison();
    }

    /** Navigate back to the Abgleich page to start a Modell-Abgleich (empty-state action). */
    goToReconciliation(): void {
        this.router.navigate(['/cn/reconciliation']);
    }

    /** Resolve a model display name from a per-model expectation (fallback: "Modell #id"). */
    modelName(model: CnModelExpectationDto): string {
        return model.modelName ?? `Modell #${model.modelId}`;
    }

    /** Whether the given model is the most lucrative one in the verdict (for card highlighting). */
    isMostLucrative(modelId: number): boolean {
        return this.result?.verdict?.mostLucrativeModelId === modelId;
    }

    /** Resolve a contract display label (fallback: "Vertrag #id"). */
    contractLabel(contract: CnContractComparisonDto): string {
        return contract.contractLabel ?? `Vertrag #${contract.contractId}`;
    }

    /** Per-model winner name for a contract (fallback: "Modell #winnerModelId"). */
    contractWinnerName(contract: CnContractComparisonDto): string {
        const match = contract.perModel.find(m => m.modelId === contract.winnerModelId);
        return match?.modelName ?? `Modell #${contract.winnerModelId}`;
    }

    /** Expected total of a model for a contract (display lookup; 0 when the model is absent). */
    cellExpected(contract: CnContractComparisonDto, modelId: number): number {
        return contract.perModel.find(m => m.modelId === modelId)?.expectedTotal ?? 0;
    }

    /** Delta (received − expected) of a model for a contract (display lookup; 0 when absent). */
    cellDelta(contract: CnContractComparisonDto, modelId: number): number {
        return contract.perModel.find(m => m.modelId === modelId)?.deltaReceivedMinusExpected ?? 0;
    }

    /**
     * Build the monthly Verlauf chart: a real per-month Ist bar series + a cumulative-Ist line
     * (running sum — the only allowed display transform) + one flat dataset per model at its
     * top-level expected total, which renders as a horizontal REFERENCE line (the engine has no
     * month dimension, so model values are NOT a monthly projection). Returns null when there is
     * no monthly data.
     */
    private buildChartData(result: CnAbgleichResultDto | null): any {
        const monthly = result?.monthly ?? [];
        if (!result || monthly.length === 0) {
            return null;
        }

        const documentStyle = getComputedStyle(document.documentElement);
        const blue = documentStyle.getPropertyValue('--blue-500') || '#3b82f6';
        const green = documentStyle.getPropertyValue('--green-500') || '#22c55e';
        const referencePalette = [
            documentStyle.getPropertyValue('--orange-500') || '#f97316',
            documentStyle.getPropertyValue('--purple-500') || '#a855f7',
            documentStyle.getPropertyValue('--cyan-500') || '#06b6d4',
            documentStyle.getPropertyValue('--pink-500') || '#ec4899',
            documentStyle.getPropertyValue('--teal-500') || '#14b8a6',
            documentStyle.getPropertyValue('--indigo-500') || '#6366f1'
        ];

        const labels = monthly.map(m => m.period);
        const istData = monthly.map(m => m.receivedTotal ?? 0);

        // Cumulative Ist (running sum) — a display-only transform, no business calculation.
        let running = 0;
        const cumulativeData = istData.map(value => {
            running += value;
            return running;
        });

        const datasets: any[] = [
            {
                type: 'bar',
                label: 'Ist (Monat)',
                data: istData,
                backgroundColor: green,
                borderColor: green,
                order: 3
            },
            {
                type: 'line',
                label: 'Ist (kumuliert)',
                data: cumulativeData,
                borderColor: blue,
                backgroundColor: blue,
                tension: 0.3,
                fill: false,
                order: 2
            }
        ];

        // One horizontal reference line per model at its flat portfolio expected total.
        (result.perModel ?? []).forEach((model, index) => {
            const color = referencePalette[index % referencePalette.length];
            const label = this.modelName(model) + (model.active ? ' (aktiv)' : '') + ' — Erwartung';
            datasets.push({
                type: 'line',
                label,
                data: labels.map(() => model.expectedTotal ?? 0),
                borderColor: color,
                backgroundColor: color,
                borderDash: [6, 4],
                pointRadius: 0,
                fill: false,
                tension: 0,
                order: 1
            });
        });

        return {labels, datasets};
    }

    /**
     * Shared chart options for the Verlauf chart, using CSS theme variables (text / grid colours)
     * so it follows the active theme — mirrors the cn dashboard "Soll vs Ist (Verlauf)" wrapper.
     */
    private buildChartOptions(): any {
        const documentStyle = getComputedStyle(document.documentElement);
        const textColor = documentStyle.getPropertyValue('--text-color') || '#495057';
        const textColorSecondary = documentStyle.getPropertyValue('--text-color-secondary') || '#6c757d';
        const surfaceBorder = documentStyle.getPropertyValue('--surface-border') || '#dee2e6';

        return {
            maintainAspectRatio: false,
            aspectRatio: 0.8,
            plugins: {
                legend: {labels: {color: textColor}}
            },
            scales: {
                x: {
                    ticks: {color: textColorSecondary},
                    grid: {color: surfaceBorder}
                },
                y: {
                    beginAtZero: true,
                    ticks: {color: textColorSecondary},
                    grid: {color: surfaceBorder}
                }
            }
        };
    }
}
